import { useColorModeValue, useToast } from "@chakra-ui/react";
import React, { useCallback, useState } from "react";
import { QuoteApi } from "../api/QuoteApi";
import { Comment } from "../lib/types/CommentType";
import { CommentsContext } from "../state/comments-context";

export interface CommentsProviderProps {
  children: any;
  id: number;
}
function CommentsContextProvider({ children, id }: CommentsProviderProps) {
  const toast = useToast();
  const toastVariant = useColorModeValue("subtle", "solid");
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState<boolean>(false);

  const fetchComments = useCallback(() => {
    if (!id) return;
    setCommentsLoading(true);

    QuoteApi.getQuoteComments(+id)
      .then((res) => {
        setComments(res.data);
      })
      .catch((err) => {
        toast({
          title: "Unable to Load Comments",
          description:
            "An error has occurred when attempting to fetch comments",
          status: "error",
          variant: toastVariant,
          isClosable: true,
          position: "top-right",
        });
      })
      .finally(() => setCommentsLoading(false));
  }, [id, toast, setComments, toastVariant]);

  const submitComment = useCallback(
    (comment: string, email: string) => {
      if (!email) return;
      setCommentsLoading(true);

      QuoteApi.createQuoteComment(+id, comment, email)
        .then((res) => {
          const newComment = res.data;
          comments.push(newComment);
          toast({
            title: "Comment Saved",
            description: "The comment has been saved",
            status: "success",
            variant: toastVariant,
            isClosable: true,
            position: "top-right",
          });
        })
        .catch((err) => {
          toast({
            title: "Unable to Save your comment",
            description:
              "An error has occurred when attempting to submit a comment",
            status: "error",
            variant: toastVariant,
            isClosable: true,
            position: "top-right",
          });
        })
        .finally(() => setCommentsLoading(false));
    },
    [comments, id, toast, toastVariant]
  );

  const deleteComment = useCallback(
    (commentId: number) => {
      if (!commentId) return;
      setCommentsLoading(true);

      QuoteApi.deactivateComment(+id, commentId)
        .then((res) => {
          setComments(
            comments.filter((comment: Comment) => {
              return comment.commentId !== commentId;
            })
          );
          toast({
            title: "Comment Deleted",
            description: "The comment has been deleted",
            status: "success",
            variant: toastVariant,
            isClosable: true,
            position: "top-right",
          });
        })
        .catch((err) => {
          toast({
            title: "Unable to delete your comment",
            description:
              "An error has occurred when attempting to delete a comment",
            status: "error",
            variant: toastVariant,
            isClosable: true,
            position: "top-right",
          });
        })
        .finally(() => setCommentsLoading(false));
    },
    [id, comments, toast, toastVariant]
  );

  React.useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  return (
    <CommentsContext.Provider
      value={{
        comments,
        submitComment,
        deleteComment,
        fetchComments,
        commentsLoading,
      }}
    >
      {children}
    </CommentsContext.Provider>
  );
}

export default CommentsContextProvider;
